import { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { toast } from "react-hot-toast";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { cleanRut } from "rutlib/lib";

import { InputIcon } from "../../../../components/inputs/input-icon";
import { ButtonLoading } from "../../../../components/buttons/button-loading";
import ClientAPI from "../../../../services/api/clients.api";
import GuestPaymentsAPI from "../../../../services/api/guest-payments.api";

const ASSIGN_ERROR_MESSAGES = {
  404: "No se encontró el pago o el cliente seleccionado",
  409: "El pago ya fue vinculado o no está disponible para vincular",
  422: "Datos inválidos para vincular el pago",
};

const getAssignErrorMessage = (error) => {
  const status = error?.response?.status;
  return ASSIGN_ERROR_MESSAGES[status] || "Ocurrió un error, intenta nuevamente";
};

const AssignModal = ({ open, toggle, payment, onAssigned }) => {
  const [name, setName] = useState("");
  const [rut, setRut] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (open) {
      setName(payment?.payer?.companyName ?? "");
      setRut(payment?.payer?.rut ?? "");
    } else {
      setName("");
      setRut("");
      setResults([]);
      setSelectedClient(null);
      setSearching(false);
      setConfirming(false);
      setSearched(false);
    }
  }, [open, payment]);

  const runSearch = (column, value) => {
    if (!value) return;
    setSearching(true);
    setSearched(true);
    setSelectedClient(null);
    ClientAPI.clientsList({
      paginationParams: { page: 1, limit: 10, search: [{ column, value }] },
    })
      .then((res) => {
        setResults(res?.data?.data ?? []);
      })
      .catch(() => {
        toast.error("No pudimos buscar clientes");
      })
      .finally(() => setSearching(false));
  };

  const handleSearchName = (e) => {
    e.preventDefault();
    runSearch("name", name.trim());
  };

  const handleSearchRut = (e) => {
    e.preventDefault();
    // El input muestra el RUT con puntos/guión (tal como llega del pago),
    // pero se normaliza antes de enviarlo al backend.
    runSearch("rut", cleanRut(rut.trim()));
  };

  const handleSelectClient = (client) => setSelectedClient(client);

  const handleConfirm = async () => {
    if (!selectedClient || !payment) return;
    setConfirming(true);
    try {
      await GuestPaymentsAPI.assign(payment.id, selectedClient.id);
      toast.success("Pago vinculado correctamente");
      onAssigned();
    } catch (error) {
      toast.error(getAssignErrorMessage(error));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={toggle} size="lg" centered>
      <div className="gm-modal-header">
        <div className="gm-modal-title-container">
          <div className="gm-modal-title">Vincular pago a un cliente</div>
        </div>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={toggle}
        />
      </div>
      <ModalBody>
        <div className="gm-modal-form-container">
          {payment && (
            <div className="gm-modal-form-section">
              <div className="gm-modal-form-section-title">Datos del pago</div>
              <div className="gm-modal-form-group">
                <div className="gm-modal-input-group">
                  <label>Empresa</label>
                  <input type="text" value={payment.payer?.companyName || ""} disabled />
                </div>
                <div className="gm-modal-input-group">
                  <label>RUT</label>
                  <input type="text" value={payment.payer?.rut || ""} disabled />
                </div>
              </div>
            </div>
          )}

          <div className="gm-modal-form-section">
            <div className="gm-modal-form-section-title">Buscar cliente</div>
            <div className="gm-modal-searchbar-group">
              <div className="gm-modal-input-group">
                <form onSubmit={handleSearchName} className="gm-search-form-inline">
                  <InputIcon
                    Icon={HiMagnifyingGlass}
                    type="text"
                    placeholder="Buscar por nombre"
                    name="search-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <button type="submit" className="gm-primary-button">
                    Buscar
                  </button>
                </form>
                <form onSubmit={handleSearchRut} className="gm-search-form-inline">
                  <InputIcon
                    Icon={HiMagnifyingGlass}
                    type="text"
                    placeholder="Buscar por RUT"
                    name="search-rut"
                    value={rut}
                    onChange={(e) => setRut(e.target.value)}
                  />
                  <button type="submit" className="gm-primary-button">
                    Buscar
                  </button>
                </form>

                {searching && <p className="gm-span-modal-input">Buscando...</p>}

                {!searching && searched && results.length === 0 && (
                  <p className="gm-span-modal-input">Sin resultados.</p>
                )}

                {!searching && results.length > 0 && (
                  <div className="gm-search-results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>RUT</th>
                          <th>Email</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((client) => (
                          <tr
                            key={client.id}
                            className={selectedClient?.id === client.id ? "gm-tr-selected" : ""}
                          >
                            <td>{client.name}</td>
                            <td>{client.rut}</td>
                            <td>{client.email}</td>
                            <td>
                              <button
                                type="button"
                                className="gm-primary-button gm-search-result-btn"
                                onClick={() => handleSelectClient(client)}
                              >
                                Vincular
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedClient && (
                  <p className="gm-span-modal-input">
                    Cliente seleccionado: <strong>{selectedClient.name}</strong> ({selectedClient.rut})
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <div
        className="gm-modal-buttons"
      >
        <button type="button" className="gm-secondary-button" onClick={toggle}>
          Cancelar
        </button>
        <ButtonLoading
          type="button"
          className="gm-primary-button"
          onClick={handleConfirm}
          loading={confirming}
          disabled={!selectedClient || confirming}
          buttonLabel="Vincular"
        />
      </div>
    </Modal>
  );
};

export default AssignModal;
